/**
 * Catálogo de indicadores económicos externos para el Widget Builder.
 * Solo se incluyen indicadores VERIFICADOS end-to-end (endpoint accesible, respuesta parseable).
 *
 * Estado actual (2026-04-16):
 *   ✅ co_trm  — TRM Colombia, datos.gov.co (Socrata), verificado
 *   ✅ int_btc — Bitcoin USD, CoinGecko, verificado
 *
 * 🗺️ ROADMAP — Pendientes de investigación:
 *   IPC Colombia  → datos.gov.co IDs cambiaron; nueva ruta: suameca.banrep.gov.co (SDMX)
 *   TPM Colombia  → Portal Banrep migró a SDMX; requiere doc de parámetros específicos
 *   FRED (USA/Int) → Verificados estructuralmente (400 = key faltante, no 404), pendiente
 *                    prueba con api_key real (gratuita en fredaccount.stlouisfed.org)
 */

export interface EconomicIndicator {
  id: string;
  bandera: string;
  pais: 'Colombia' | 'USA' | 'Internacional';
  categoria: 'Divisa' | 'Precios' | 'Tasas' | 'Laboral' | 'Macro' | 'Commodities' | 'Cripto';
  nombre: string;
  descripcion: string;
  unidad: string;
  frecuencia: string;
  endpoint: string;
  metodo: 'GET' | 'POST';
  /** Params estáticos que se agregan a la URL (sin api_key — se agrega dinámicamente) */
  params: Record<string, string>;
  /** Path al campo valor en la respuesta (dot-notation, ej: "valor", "observations[0].value") */
  campo_valor: string;
  /** Path al campo fecha en la respuesta */
  campo_fecha: string;
  requiere_key: boolean;
  key_nombre?: string;
  key_url?: string;
  notas?: string;
}

export const INDICATORS_CATALOG: EconomicIndicator[] = [
  // ────────────────────────────────────────────────────────────
  // 🇨🇴 COLOMBIA
  // ────────────────────────────────────────────────────────────
  {
    id: 'co_trm',
    bandera: '🇨🇴',
    pais: 'Colombia',
    categoria: 'Divisa',
    nombre: 'Dólar (TRM)',
    descripcion: 'Tasa Representativa del Mercado — valor oficial del dólar',
    unidad: 'COP / USD',
    frecuencia: 'Diaria',
    endpoint: 'https://www.datos.gov.co/resource/32sa-8pi3.json',
    metodo: 'GET',
    params: { $limit: '1', $order: 'vigenciadesde DESC' },
    campo_valor: '[0].valor',
    campo_fecha: '[0].vigenciadesde',
    requiere_key: false,
    notas: 'API Socrata (datos.gov.co). El valor viene como string; se parsea a float en el widget.',
  },

  // ────────────────────────────────────────────────────────────
  // 🌍 INTERNACIONAL
  // ────────────────────────────────────────────────────────────
  {
    id: 'int_btc',
    bandera: '🌍',
    pais: 'Internacional',
    categoria: 'Cripto',
    nombre: 'Bitcoin (BTC/USD)',
    descripcion: 'Precio del Bitcoin en tiempo real — CoinGecko',
    unidad: 'USD',
    frecuencia: 'Tiempo real',
    endpoint: 'https://api.coingecko.com/api/v3/simple/price',
    metodo: 'GET',
    params: { ids: 'bitcoin', vs_currencies: 'usd' },
    campo_valor: 'bitcoin.usd',
    campo_fecha: '',
    requiere_key: false,
    notas: 'Límite: ~30 req/min sin key. Usar refresh_policy mínimo 3600s para no exceder el rate limit.',
  },
];

export const INDICATOR_PAISES = ['Todos', 'Colombia', 'Internacional'] as const;
export const INDICATOR_CATEGORIAS = ['Todas', 'Divisa', 'Cripto'] as const;

export function getIndicatorById(id: string): EconomicIndicator | undefined {
  return INDICATORS_CATALOG.find(i => i.id === id);
}

/**
 * Construye la URL completa del indicador con params + api_key opcional.
 *
 * IMPORTANTE: No usa URLSearchParams porque codifica '$' como '%24',
 * lo que rompe las APIs Socrata (datos.gov.co) que requieren '$literal'
 * en parámetros como $limit, $order, $where.
 * Las claves se pasan sin codificar; solo los valores se codifican.
 */
export function buildIndicatorUrl(indicator: EconomicIndicator, apiKey?: string): string {
  const parts: string[] = [];

  for (const [k, v] of Object.entries(indicator.params)) {
    parts.push(`${k}=${encodeURIComponent(v)}`);
  }

  if (indicator.requiere_key && apiKey) {
    parts.push(`api_key=${encodeURIComponent(apiKey)}`);
  }

  if (parts.length === 0) return indicator.endpoint;

  const separator = indicator.endpoint.includes('?') ? '&' : '?';
  return `${indicator.endpoint}${separator}${parts.join('&')}`;
}
